import { useLocale as useLocaleR42 } from '@/lib/locale';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Captions, Pause, Play, RotateCcw, Volume2, VolumeX } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Skeleton } from '@/components/ui/skeleton';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { SPLASH_COMMERCIAL } from '@/lib/splashCommercial';

/**
 * PortraitVideoCard — R12 phone-frame treatment for the 9:16 Splash commercial.
 *
 * Behaviour (carried over from the R11 card, UI rebuilt per /app/design_guidelines.md):
 *   • muted autoplay when ≥60% visible; auto-pauses when scrolled away / tab hidden
 *   • never auto-resumes audible playback or a clip the visitor explicitly paused
 *   • honours prefers-reduced-motion (poster + play button, no autoplay)
 *   • MP4 primary → WebM fallback; retry on hard failure
 *   • ONE aligned control cluster (play/pause + sound) bottom-left inside the screen
 */
const reducedMotion = () => typeof window !== 'undefined' && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
const fmt = (s) => { const n = Math.max(0, Math.floor(s || 0)); return `${Math.floor(n / 60)}:${String(n % 60).padStart(2, '0')}`; };

export const PortraitVideoCard = ({ testId = 'meet-splash-video', className = '', size = 'md' }) => {
    const { t: t42 } = useLocaleR42();
    const videoRef = useRef(null);
    const frameRef = useRef(null);
    const visibleRef = useRef(false);
    const userPausedRef = useRef(false);
    const automaticPauseRef = useRef(false);
    const reducedRef = useRef(reducedMotion());
    const attachVideo = useCallback((node) => {
        if (!node && videoRef.current) { automaticPauseRef.current = true; videoRef.current.pause(); }
        videoRef.current = node;
    }, []);
    const [activated, setActivated] = useState(false);
    const [muted, setMuted] = useState(true);
    const [playing, setPlaying] = useState(false);
    const [loaded, setLoaded] = useState(false);
    const [blocked, setBlocked] = useState(false);
    const [errored, setErrored] = useState(false);
    const [fallback, setFallback] = useState(false);
    const [attempt, setAttempt] = useState(0);
    const [reduce, setReduce] = useState(reducedMotion);
    const [progress, setProgress] = useState(0);
    const [remaining, setRemaining] = useState(SPLASH_COMMERCIAL.durationSeconds);

    const pauseAutomatically = useCallback(() => {
        const video = videoRef.current;
        if (video && !video.paused) { automaticPauseRef.current = true; video.pause(); }
    }, []);

    const playIfAllowed = useCallback(() => {
        const video = videoRef.current;
        if (!video || !video.currentSrc || !visibleRef.current || document.hidden
            || reducedRef.current || userPausedRef.current || !video.muted || video.ended) return;
        video.play()?.then(() => setBlocked(false)).catch((error) => { if (error.name !== 'AbortError') setBlocked(true); });
    }, []);

    useEffect(() => {
        const query = window.matchMedia('(prefers-reduced-motion: reduce)');
        const onChange = () => { reducedRef.current = query.matches; setReduce(query.matches); if (query.matches) pauseAutomatically(); };
        query.addEventListener('change', onChange);
        const onVisibility = () => (document.hidden ? pauseAutomatically() : playIfAllowed());
        document.addEventListener('visibilitychange', onVisibility);
        const observer = typeof IntersectionObserver !== 'undefined' ? new IntersectionObserver(([entry]) => {
            if (entry.isIntersecting) setActivated(true);
            if (entry.intersectionRatio >= 0.6) { visibleRef.current = true; playIfAllowed(); }
            else if (entry.intersectionRatio < 0.3) { visibleRef.current = false; pauseAutomatically(); }
        }, { threshold: [0, 0.3, 0.6] }) : null;
        if (observer && frameRef.current) observer.observe(frameRef.current);
        if (!observer) { visibleRef.current = true; setActivated(true); }
        return () => {
            observer?.disconnect();
            query.removeEventListener('change', onChange);
            document.removeEventListener('visibilitychange', onVisibility);
            pauseAutomatically();
        };
    }, [pauseAutomatically, playIfAllowed]);

    const playByChoice = () => {
        const video = videoRef.current;
        if (!video) return;
        userPausedRef.current = false;
        setActivated(true);
        if (!video.getAttribute('src')) { video.src = fallback ? SPLASH_COMMERCIAL.webm : SPLASH_COMMERCIAL.mp4; video.load(); }
        document.querySelectorAll('video').forEach((v) => { if (v !== video && !v.paused) v.pause(); });
        if (video.ended) video.currentTime = 0;
        video.play()?.then(() => setBlocked(false)).catch((error) => { if (error.name !== 'AbortError') setBlocked(true); });
    };
    const togglePlay = () => {
        const video = videoRef.current;
        if (!video) return;
        if (video.paused) playByChoice();
        else { userPausedRef.current = true; automaticPauseRef.current = false; video.pause(); }
    };
    const toggleSound = () => {
        const video = videoRef.current;
        if (!video) return;
        if (video.muted || video.volume === 0) { video.volume = 1; video.muted = false; setMuted(false); playByChoice(); }
        else { video.muted = true; setMuted(true); }
    };
    const onError = () => {
        if (!activated) return;
        setLoaded(false); setPlaying(false);
        if (!fallback) { setFallback(true); setMuted(true); } else setErrored(true);
    };
    const retry = () => { userPausedRef.current = false; setErrored(false); setLoaded(false); setMuted(true); setFallback(false); setActivated(true); setAttempt((c) => c + 1); };
    const onTime = () => {
        const v = videoRef.current;
        if (!v || !v.duration) return;
        setProgress((v.currentTime / v.duration) * 100);
        setRemaining(v.duration - v.currentTime);
    };

    const width = size === 'sm' ? 'max-w-[280px]' : size === 'lg' ? 'max-w-[380px]' : 'max-w-[340px]';
    const ctl = 'sd-focus inline-flex h-11 w-11 items-center justify-center rounded-full bg-white text-[color:var(--sp-navy)] transition-colors duration-200 hover:bg-[rgba(10,22,40,0.06)] disabled:opacity-50';

    return (
        <TooltipProvider delayDuration={200}>
            <div className={`relative mx-auto w-full ${width} ${className}`} data-testid={`${testId}-card`}>
                {/* soft brand glow behind the device */}
                <div aria-hidden="true" className="pointer-events-none absolute -inset-10 -z-10 rounded-[3rem] bg-[radial-gradient(circle_at_30%_20%,rgba(0,212,255,0.26),transparent_55%),radial-gradient(circle_at_70%_70%,rgba(255,107,53,0.14),transparent_60%)] blur-2xl" />
                <div className="relative overflow-hidden rounded-[2.4rem] border border-[rgba(10,22,40,0.16)] bg-[#0a1628] p-2 shadow-[var(--sp-shadow-lg)]">
                    <div ref={frameRef} className="group relative aspect-[9/16] overflow-hidden rounded-[1.9rem] bg-black" data-testid={`${testId}-frame`}>
                        {/* captions are burned into the commercial itself */}
                        <video key={`${fallback}-${attempt}`} ref={attachVideo}
                            src={activated ? (fallback ? SPLASH_COMMERCIAL.webm : SPLASH_COMMERCIAL.mp4) : undefined}
                            poster={SPLASH_COMMERCIAL.poster} muted={muted} playsInline preload={activated ? 'metadata' : 'none'}
                            className="h-full w-full cursor-pointer object-cover" onClick={togglePlay}
                            aria-label={t42("Meet Splash: a 90-second commercial showing coach requests, previews and confirmed account changes. On-screen captions included.")}
                            data-testid={`${testId}-el`}
                            onLoadedMetadata={() => { setLoaded(true); setErrored(false); playIfAllowed(); }}
                            onCanPlay={playIfAllowed}
                            onTimeUpdate={onTime}
                            onPlay={() => { automaticPauseRef.current = false; userPausedRef.current = false; setPlaying(true); setBlocked(false); }}
                            onPause={() => { setPlaying(false); if (!automaticPauseRef.current && !videoRef.current?.ended) userPausedRef.current = true; automaticPauseRef.current = false; }}
                            onEnded={() => { setPlaying(false); userPausedRef.current = true; }}
                            onVolumeChange={() => { const v = videoRef.current; if (v) setMuted(v.muted || v.volume === 0); }}
                            onError={onError}
                        />
                        {activated && !loaded && !errored && <Skeleton className="pointer-events-none absolute inset-0 rounded-none opacity-20" data-testid={`${testId}-loading`} />}

                        {/* phone notch */}
                        <div aria-hidden="true" className="pointer-events-none absolute left-1/2 top-2.5 z-10 h-5 w-24 -translate-x-1/2 rounded-full bg-[#0a1628]" />

                        {/* centre play affordance when paused */}
                        {!playing && !errored && (
                            <button type="button" onClick={togglePlay} aria-label={t42("Play the Splash commercial")}
                                className="sd-focus absolute left-1/2 top-1/2 z-10 flex h-16 w-16 -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full bg-white/90 text-[color:var(--sp-navy)] shadow-[var(--sp-shadow-md)] backdrop-blur transition-transform duration-200 hover:scale-105"
                                data-testid={`${testId}-center-play`}>
                                <Play className="ml-0.5 h-7 w-7" fill="currentColor" aria-hidden="true" />
                            </button>
                        )}

                        {/* bottom scrim + aligned control cluster */}
                        <div aria-hidden="true" className="pointer-events-none absolute inset-x-0 bottom-0 h-28 bg-gradient-to-t from-black/55 to-transparent" />
                        <div className="absolute inset-x-3 bottom-3 z-10 flex items-center justify-between gap-2">
                            <div className="flex items-center gap-1 rounded-full border border-[rgba(10,22,40,0.10)] bg-white/90 p-1 shadow-[var(--sp-shadow-sm)] backdrop-blur" data-testid={`${testId}-controls`}>
                                <Tooltip>
                                    <TooltipTrigger asChild>
                                        <button type="button" onClick={togglePlay} disabled={errored} aria-pressed={playing}
                                            aria-label={playing ? t42("Pause Splash commercial") : t42("Play Splash commercial")} className={ctl} data-testid={`${testId}-play-toggle`}>
                                            {playing ? <Pause className="h-5 w-5" fill="currentColor" aria-hidden="true" /> : <Play className="ml-0.5 h-5 w-5" fill="currentColor" aria-hidden="true" />}
                                        </button>
                                    </TooltipTrigger>
                                    <TooltipContent>{playing ? t42("Pause") : t42("Play")}</TooltipContent>
                                </Tooltip>
                                <Tooltip>
                                    <TooltipTrigger asChild>
                                        <button type="button" onClick={toggleSound} disabled={errored} aria-pressed={!muted}
                                            aria-label={muted ? t42("Turn sound on") : t42("Mute")} className={ctl} data-testid={`${testId}-mute-toggle`}>
                                            {muted ? <VolumeX className="h-5 w-5" aria-hidden="true" /> : <Volume2 className="h-5 w-5" aria-hidden="true" />}
                                        </button>
                                    </TooltipTrigger>
                                    <TooltipContent>{muted ? t42("Turn sound on") : t42("Mute")}</TooltipContent>
                                </Tooltip>
                                <span className={`select-none whitespace-nowrap rounded-full px-2.5 text-xs font-semibold ${muted ? 'text-[color:var(--sp-text-muted)]' : 'text-[color:var(--sp-navy)]'}`} data-testid={`${testId}-sound-label`}>
                                    {muted ? t42("Tap for sound") : t42("Sound on")}
                                </span>
                            </div>
                            <span className="rounded-full bg-black/55 px-2.5 py-1 text-[11px] font-semibold tabular-nums text-white backdrop-blur" data-testid={`${testId}-time`}>{fmt(remaining)}</span>
                        </div>
                        {/* minimal progress indicator */}
                        <div aria-hidden="true" className="absolute inset-x-0 bottom-0 z-10 h-1 bg-white/15">
                            <div className="h-full bg-[var(--sp-cyan)]" style={{ width: `${progress}%` }} />
                        </div>
                    </div>
                </div>

                <p className="mt-3 flex items-center justify-center gap-1.5 text-center text-xs text-[color:var(--sp-text-subtle)]" data-testid={`${testId}-status`} aria-live="polite">
                    <Captions className="h-3.5 w-3.5" aria-hidden="true" />
                    {reduce && !playing ? t42("Motion preference respected — press play when ready.") : blocked ? t42("Autoplay unavailable — press play.") : t42("90-second commercial · captions on screen")}
                </p>
                {errored && (
                    <Alert className="mt-3" data-testid={`${testId}-error`}>
                        <AlertDescription data-testid={`${testId}-error-message`}>{t42("The commercial couldn’t load. You can keep browsing.")}</AlertDescription>
                        <Button type="button" variant="outline" className="mt-2 min-h-11" onClick={retry} data-testid={`${testId}-retry-button`}><RotateCcw className="mr-2 h-4 w-4" />{t42("Try again")}</Button>
                    </Alert>
                )}
            </div>
        </TooltipProvider>
    );
};

export default PortraitVideoCard;
